import { Injectable } from '@angular/core';
import { StreamChat, Channel, MessageResponse, ChannelSort } from 'stream-chat';
import { environment } from '@env/environment';
import { Subject, Subscription } from 'rxjs';
import {
  ChatChannel,
  ChatChannelPayload,
  ChatEligibilityPayload,
  ChatService as ChatApiService
} from '@vestiaire/api-sdk-angular';
import { map, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';
import { getUser } from '@app/core/user/store/reducers';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  streamChat: StreamChat;
  connected$ = new Subject<boolean>();
  connectionState$ = this.connected$.asObservable();

  // We use the following as a "hook" before sending a message.
  // @see ChatContent subscribe
  private _sendingMessage = new Subject<string>();
  sendingMessage = this._sendingMessage.asObservable();
  storeListener: Subscription;
  private _connectUser: boolean;

  constructor(private chatApiService: ChatApiService, private store: Store<IAppState>) {}

  init() {
    this._connectUser = true;
    this.streamChat = new StreamChat(environment.streamApiKey);
    this.streamChat.setBaseURL('https://chat-proxy-dublin.stream-io-api.com');
    this.storeListener = this.store.pipe(select(getUser), filter(Boolean)).subscribe({
      next: ({ id }) => {
        if (this._connectUser && id) {
          this.connect(id);
        }
      }
    });
  }

  async connect(userID: string, token?: string) {
    const user = { id: userID };
    const tokenProvider = () => {
      console.log('Refreshed chat token at ', new Date());
      return this.chatApiService
        .refreshToken()
        .pipe(map(res => res.data.userToken.token))
        .toPromise();
    };

    this.streamChat
      .connectUser(user, tokenProvider)
      .then(_ => this.connected$.next(true))
      .catch(_ => this.connected$.next(false));
  }

  // check chat elligibility
  async checkChatEligibility(payload: ChatEligibilityPayload): Promise<boolean> {
    const res = await this.chatApiService.getEligibility(payload).toPromise();
    if (res?.data) {
      return Promise.resolve(res.data.eligibility);
    }

    return Promise.reject(`Error in checking eligibility for the chat`);
  }

  // connect to VC api. VC api checks if channel exists otherwise create a new channel
  async createChannel(payload: ChatChannelPayload): Promise<ChatChannel> {
    const channels = await this.chatApiService.createChannel(payload).toPromise();
    if (channels?.data) {
      return Promise.resolve(channels.data);
    }

    return Promise.reject(`Cannot create or find channel:`);
  }

  // connect to stream
  async joinChannel(channelId: string): Promise<Channel> {
    const query = { id: channelId, members: { $in: [this.streamChat.user.id] } };
    const sort: ChannelSort = { last_message_at: -1 };
    const options = {
      watch: true,
      state: true
    };

    const channelsStream = await this.streamChat.queryChannels(query, sort, options);
    if (channelsStream && channelsStream.length && channelsStream[0].id === channelId) {
      return Promise.resolve(channelsStream[0]);
    }

    return Promise.reject(`Cannot find channelId: ${channelId}`);
  }

  // when the user is blocked by his interlocutor (chanel banned) but can chat with other members
  async getBlockedMembers(channel: Channel): Promise<string[]> {
    try {
      const { members } = await channel.queryMembers({ banned: true });
      return members.map(member => member.user.id);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  // when the user is globally banned from moderation and cannot chat with any member anymore
  async getBannedMembers(channel: Channel): Promise<string[]> {
    try {
      const { members: banned } = await channel.queryMembers({});
      return banned.filter(member => member.user.banned === true).map(member => member.user.id);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  banUserFromChannel(channel: Channel, userId: string) {
    return channel.banUser(userId, {});
  }

  unbanUserFromChannel(channel: Channel, userId: string) {
    return channel.unbanUser(userId);
  }

  getLastMessages(channel: Channel): MessageResponse[] {
    return (channel.state.messages as unknown) as MessageResponse[];
  }

  getMessages(channel: Channel, beforeId?: string, limit = 25): Promise<MessageResponse[]> {
    return channel
      .query({
        messages: {
          limit,
          id_lt: beforeId
        }
      })
      .then(({ messages }) => messages || []);
  }

  onSendMessage(text: string) {
    this._sendingMessage.next(text);
  }

  sendMessage(channel: Channel, text: string): Promise<MessageResponse> {
    return channel
      .sendMessage({
        text
      })
      .then(({ message }) => message);
  }

  translateMessage(messageID: string, locale: string): Promise<any> {
    return this.streamChat.translateMessage(messageID, locale).then(res => res.message);
  }

  async markAsRead(channel: Channel) {
    await channel.markRead();
  }

  async logout() {
    if (this.streamChat?.user?.online) {
      try {
        this._connectUser = false;
        await this.streamChat.disconnect();
        this.storeListener.unsubscribe();
        this.connected$.next(false);
      } catch (error) {
        console.error(error);
      }
    }
  }
}
